/**
 * 四种非可分离混合模式（M1-12 / DESIGN.md §4.4）。
 *
 * 「非可分离」是指各通道**不能**独立算：hue/saturation/color/luminosity 要先把
 * 三个通道当成一个颜色去取它的亮度与饱和度，再把另一组的亮度/饱和度装回去。
 * 这也是它们没法跟那十二种可分离模式共用一个 `vec3` 表达式的原因。
 *
 * 公式逐条对照 **CSS Compositing and Blending Level 1 §5.2**。
 * 与可分离模式一样，公式只写一份，两种语言由方言层渲染。
 *
 * 两个实现上的要点：
 *
 * 1. **SetSat 不需要排序。** 规范用「找出最大/中间/最小通道再分别赋值」来描述，
 *    照抄会写出一堆通道比较，还会在两个通道相等时出错。但那段描述的效果就是把
 *    `[min, max]` 线性映射到 `[0, s]`——写成 `(c - min) * s / (max - min)`
 *    逐分量即可，最大值自然得到 s、最小值得到 0、中间值得到规范要求的比例。
 * 2. **ClipColor 的除法要防零。** `l - n` 与 `x - l` 都可能是 0（比如三个通道
 *    相等时），而这里除出来的 NaN 会一路传到像素上。
 */
import type { ShaderDialect } from './dialect';

/**
 * 非可分离混合模式的名字。前 4 种顺序与 CSS 规范一致；"深色"/"浅色"
 * 是 Photoshop 独有、规范未收录的模式，追加在末尾（原因同 modes.ts 的
 * `SEPARABLE_BLEND_MODES` 头部注释：序号是着色器与 CPU 之间的约定）。
 * 它们要比较**整像素**明度取其中一个原样返回，不是逐通道算，所以归
 * 非可分离一类，不是因为公式复杂。
 */
export const NONSEPARABLE_BLEND_MODES = [
  'hue',
  'saturation',
  'color',
  'luminosity',
  'darkerColor',
  'lighterColor',
] as const;

export type NonSeparableBlendMode = (typeof NONSEPARABLE_BLEND_MODES)[number];

/**
 * 亮度权重。CSS 规范 §5.2 写死的就是这三个数（0.3 / 0.59 / 0.11），
 * 不是 Rec.709 的 0.2126/0.7152/0.0722——换成后者会让 luminosity 模式整体偏色。
 */
export const LUMA_WEIGHTS = [0.3, 0.59, 0.11] as const;

/** 除零保护的下限。 */
const EPSILON = 1e-6;

/** 非可分离模式共用的辅助函数（亮度、饱和度、装回去）。 */
export function nonSeparableHelpers(d: ShaderDialect): string {
  const [wr, wg, wb] = LUMA_WEIGHTS;

  const lum = d.functionDef(
    'ns_lum',
    [{ name: 'c', type: 'vec3' }],
    'float',
    `return dot(c, ${d.vec3}(${d.scalar(wr)}, ${d.scalar(wg)}, ${d.scalar(wb)}));`,
  );

  // 规范的 ClipColor：把越界的分量按亮度往回收，而不是简单 clamp——
  // clamp 会改变色相，收缩则保持
  const clipColor = d.functionDef(
    'ns_clip_color',
    [{ name: 'c', type: 'vec3' }],
    'vec3',
    [
      d.localOf('float', 'l', 'ns_lum(c)'),
      d.localOf('float', 'n', 'min(c.r, min(c.g, c.b))'),
      d.localOf('float', 'x', 'max(c.r, max(c.g, c.b))'),
      // 分母可能为 0（三通道相等时 l == n），必须夹住
      d.local(
        'lowFixed',
        `${d.vec3}(l) + (c - ${d.vec3}(l)) * l / max(l - n, ${d.scalar(EPSILON)})`,
      ),
      d.local('afterLow', d.select('c', 'lowFixed', vecCond(d, `(n < ${d.scalar(0)})`))),
      d.localOf('float', 'x2', 'max(afterLow.r, max(afterLow.g, afterLow.b))'),
      d.localOf('float', 'l2', 'ns_lum(afterLow)'),
      d.local(
        'highFixed',
        `${d.vec3}(l2) + (afterLow - ${d.vec3}(l2)) * (${d.scalar(1)} - l2) / ` +
          `max(x2 - l2, ${d.scalar(EPSILON)})`,
      ),
      `return ${d.select('afterLow', 'highFixed', vecCond(d, `(x2 > ${d.scalar(1)})`))};`,
    ].join('\n'),
  );

  const setLum = d.functionDef(
    'ns_set_lum',
    [
      { name: 'c', type: 'vec3' },
      { name: 'l', type: 'float' },
    ],
    'vec3',
    `return ns_clip_color(c + ${d.vec3}(l - ns_lum(c)));`,
  );

  const sat = d.functionDef(
    'ns_sat',
    [{ name: 'c', type: 'vec3' }],
    'float',
    'return max(c.r, max(c.g, c.b)) - min(c.r, min(c.g, c.b));',
  );

  /**
   * SetSat。见文件头第 1 点：这是规范那段「找最大/中间/最小」描述的等价闭式，
   * 不需要任何通道比较，也不会在两通道相等时出错。
   */
  const setSat = d.functionDef(
    'ns_set_sat',
    [
      { name: 'c', type: 'vec3' },
      { name: 's', type: 'float' },
    ],
    'vec3',
    [
      d.localOf('float', 'mn', 'min(c.r, min(c.g, c.b))'),
      d.localOf('float', 'mx', 'max(c.r, max(c.g, c.b))'),
      d.localOf('float', 'range', 'mx - mn'),
      d.local('scaled', `(c - ${d.vec3}(mn)) * s / max(range, ${d.scalar(EPSILON)})`),
      // range 为 0（三通道相等，本来就没有饱和度）时规范要求整体归零
      `return ${d.select(d.splat(0), 'scaled', vecCond(d, `(range > ${d.scalar(0)})`))};`,
    ].join('\n'),
  );

  // 深色/浅色本身需要用到 select/vecCond 这类底层方言原语，放进公式本身会让
  // 公式字符串在两个方言下天然不同——这里包成辅助函数，公式只留一句函数调用，
  // 跟 hue/saturation/color/luminosity 那四个"公式与方言无关"保持同一个不变式
  const darkerColorFn = d.functionDef(
    'ns_darker_color',
    [
      { name: 'cb', type: 'vec3' },
      { name: 'cs', type: 'vec3' },
    ],
    'vec3',
    `return ${d.select('cs', 'cb', vecCond(d, '(ns_lum(cb) <= ns_lum(cs))'))};`,
  );
  const lighterColorFn = d.functionDef(
    'ns_lighter_color',
    [
      { name: 'cb', type: 'vec3' },
      { name: 'cs', type: 'vec3' },
    ],
    'vec3',
    `return ${d.select('cs', 'cb', vecCond(d, '(ns_lum(cb) >= ns_lum(cs))'))};`,
  );

  return [lum, clipColor, setLum, sat, setSat, darkerColorFn, lighterColorFn].join('\n\n');
}

/** 四种模式的公式，全部由上面的辅助函数拼成。 */
export const NONSEPARABLE_FORMULAS: Readonly<Record<NonSeparableBlendMode, (d: ShaderDialect) => string>> =
  {
    hue: () => 'return ns_set_lum(ns_set_sat(cs, ns_sat(cb)), ns_lum(cb));',
    saturation: () => 'return ns_set_lum(ns_set_sat(cb, ns_sat(cs)), ns_lum(cb));',
    color: () => 'return ns_set_lum(cs, ns_lum(cb));',
    luminosity: () => 'return ns_set_lum(cb, ns_lum(cs));',
    // 深色/浅色：整像素明度较低/较高的那一个原样返回，不逐通道混——
    // select/vecCond 那部分放进了上面 nonSeparableHelpers 的辅助函数里
    darkerColor: () => 'return ns_darker_color(cb, cs);',
    lighterColor: () => 'return ns_lighter_color(cb, cs);',
  };

/** 混合函数在着色器里的名字。 */
export function nonSeparableFunctionName(mode: NonSeparableBlendMode): string {
  return `blend_${mode}`;
}

/** 把标量条件铺成向量条件，供逐分量 select 使用。 */
function vecCond(d: ShaderDialect, scalarCond: string): string {
  return d.name === 'wgsl' ? `vec3<bool>(${scalarCond})` : `bvec3(${scalarCond})`;
}

/* ------------------------------------------------------------------ */
/* CPU 侧参考实现                                                        */
/* ------------------------------------------------------------------ */

type Rgb = readonly [number, number, number];

const lum = (c: Rgb): number =>
  c[0] * LUMA_WEIGHTS[0] + c[1] * LUMA_WEIGHTS[1] + c[2] * LUMA_WEIGHTS[2];

const sat = (c: Rgb): number => Math.max(...c) - Math.min(...c);

function clipColor(c: Rgb): Rgb {
  const l = lum(c);
  const n = Math.min(...c);
  let out: Rgb = c;
  if (n < 0) {
    const denom = l - n;
    out = out.map((v) => l + ((v - l) * l) / (denom === 0 ? EPSILON : denom)) as unknown as Rgb;
  }
  const x2 = Math.max(...out);
  const l2 = lum(out);
  if (x2 > 1) {
    const denom = x2 - l2;
    out = out.map(
      (v) => l2 + ((v - l2) * (1 - l2)) / (denom === 0 ? EPSILON : denom),
    ) as unknown as Rgb;
  }
  return out;
}

function setLum(c: Rgb, l: number): Rgb {
  const d = l - lum(c);
  return clipColor([c[0] + d, c[1] + d, c[2] + d]);
}

function setSat(c: Rgb, s: number): Rgb {
  const mn = Math.min(...c);
  const mx = Math.max(...c);
  const range = mx - mn;
  if (range <= 0) return [0, 0, 0];
  return [((c[0] - mn) * s) / range, ((c[1] - mn) * s) / range, ((c[2] - mn) * s) / range];
}

/**
 * 与着色器同源的 CPU 参考实现，照 CSS 规范手写。
 *
 * 与可分离模式那份一样，它的价值在于**独立**：GPU 跑的是生成出来的源码，
 * 这里跑的是照规范写的公式，两者一致才说明生成器没把公式渲染歪。
 */
export const REFERENCE_NONSEPARABLE: Readonly<
  Record<NonSeparableBlendMode, (cb: Rgb, cs: Rgb) => Rgb>
> = {
  hue: (cb, cs) => setLum(setSat(cs, sat(cb)), lum(cb)),
  saturation: (cb, cs) => setLum(setSat(cb, sat(cs)), lum(cb)),
  color: (cb, cs) => setLum(cs, lum(cb)),
  luminosity: (cb, cs) => setLum(cb, lum(cs)),
  darkerColor: (cb, cs) => (lum(cb) <= lum(cs) ? cb : cs),
  lighterColor: (cb, cs) => (lum(cb) >= lum(cs) ? cb : cs),
};

export const REFERENCE_HELPERS = { lum, sat, setLum, setSat, clipColor };
